import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { G } from './theme'

/**
 * 선택 상자 — 브라우저 기본 목록(native <select>)을 쓰지 않는다(사용자 지시 2026-10-19).
 *   닫힌 모양은 회원가입 입력칸과 같은 어두운 칸 + 오른쪽 ▼, 펼친 목록은 페이지 위에 직접 그린다.
 *   목록은 body 에 붙여(portal) 가입 창 안쪽 스크롤에 잘리지 않게 하고, 아래 공간이 모자라면 위로 펼친다.
 *   바깥을 누르면 닫히고, 가입 창이 스크롤되면 목록이 칸을 따라간다(칸이 화면 밖으로 나가면 닫힘). 키보드: ↑↓ 이동, Enter 선택, Esc 닫기.
 * @param {{ value: any, options: Array<{ value: any, label: string }>, onChange: (v: any) => void,
 *           placeholder?: string, error?: boolean, style?: object, ariaLabel?: string, maxHeight?: number }} props
 */
export default function GoldSelect({ value, options, onChange, placeholder = '', error = false, style, ariaLabel, maxHeight = 260 }) {
  const [open, setOpen] = useState(false)
  const [focusIdx, setFocusIdx] = useState(-1)
  const [pos, setPos] = useState(null)
  const btnRef = useRef(null)
  const listRef = useRef(null)
  const current = options.find((o) => String(o.value) === String(value))

  const place = () => {
    const r = btnRef.current?.getBoundingClientRect()
    if (!r) return
    if (r.bottom < 0 || r.top > window.innerHeight) { setOpen(false); return }
    const below = window.innerHeight - r.bottom - 8
    const above = r.top - 8
    const h = Math.min(maxHeight, options.length * 36 + 8)
    const up = below < Math.min(h, 160) && above > below
    setPos({ left: r.left, width: r.width, top: up ? undefined : r.bottom + 4, bottom: up ? window.innerHeight - r.top + 4 : undefined, maxHeight: Math.max(120, Math.min(maxHeight, up ? above : below)) })
  }

  useLayoutEffect(() => {
    if (!open) return
    place()
    const idx = options.findIndex((o) => String(o.value) === String(value))
    setFocusIdx(idx)
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  // 고른 항목이 보이게 목록을 옮긴다
  useEffect(() => {
    if (!open || !pos) return
    const list = listRef.current
    const el = list?.children[Math.max(0, focusIdx)]
    if (el && list && (el.offsetTop < list.scrollTop || el.offsetTop + el.offsetHeight > list.scrollTop + list.clientHeight)) {
      list.scrollTop = el.offsetTop - list.clientHeight / 2 + el.offsetHeight / 2
    }
  }, [open, pos, focusIdx])

  useEffect(() => {
    if (!open) return
    const onDown = (e) => {
      if (btnRef.current?.contains(e.target) || listRef.current?.contains(e.target)) return
      setOpen(false)
    }
    // 칸을 품은 스크롤(가입 창·페이지)만 따라간다 — 공지 흐름 글처럼 다른 곳 스크롤은 무시
    const onScroll = (e) => {
      const t = e.target
      if (listRef.current?.contains(t)) return
      if (t === document || t === document.documentElement || (t instanceof Node && t.contains(btnRef.current))) place()
    }
    const onResize = () => place()
    document.addEventListener('mousedown', onDown)
    document.addEventListener('touchstart', onDown, { passive: true })
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('touchstart', onDown)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
    }
  }, [open])

  const pick = (o) => { onChange(o.value); setOpen(false); btnRef.current?.focus() }

  const onKeyDown = (e) => {
    if (!open && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) { e.preventDefault(); setOpen(true); return }
    if (!open) return
    if (e.key === 'Escape') { e.preventDefault(); setOpen(false); return }
    if (e.key === 'ArrowDown') { e.preventDefault(); setFocusIdx((i) => Math.min(options.length - 1, i + 1)) }
    if (e.key === 'ArrowUp') { e.preventDefault(); setFocusIdx((i) => Math.max(0, i - 1)) }
    if (e.key === 'Enter' && focusIdx >= 0) { e.preventDefault(); pick(options[focusIdx]) }
  }

  return (
    <>
      <Box
        ref={btnRef}
        type="button"
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={open}
        $error={error}
        $open={open}
        style={style}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onKeyDown}
      >
        <span className={current ? undefined : 'placeholder'}>{current ? current.label : placeholder}</span>
        <Caret $open={open} aria-hidden="true" />
      </Box>
      {open && pos && createPortal(
        <List
          ref={listRef}
          role="listbox"
          style={{ left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, maxHeight: pos.maxHeight }}
        >
          {options.map((o, i) => {
            const active = String(o.value) === String(value)
            return (
              <Item
                key={`${o.value}`}
                role="option"
                aria-selected={active}
                $active={active}
                $focus={i === focusIdx}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setFocusIdx(i)}
                onClick={() => pick(o)}
              >
                {o.label}
              </Item>
            )
          })}
        </List>,
        document.body,
      )}
    </>
  )
}

const ERROR_RED = '#ff4d5e'

const Box = styled.button`
  && {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    height: 40px;
    padding: 0 32px 0 12px;
    border: 0;
    border-radius: 4px;
    background: ${G.bg};
    font-size: 14px;
    color: ${G.sand};
    line-height: 18px; /* 사이트 공통 button { line-height: 0 } 때문에 글자가 잘리지 않게 */
    text-align: left;
    cursor: pointer;
    outline: none;
    box-shadow: ${({ $error, $open }) => ($error ? `inset 0 0 0 1px ${ERROR_RED}` : $open ? `inset 0 0 0 1px ${G.line}` : 'none')};
  }
  > span {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  > span.placeholder { opacity: 0.7; }
  &:focus-visible { box-shadow: inset 0 0 0 1px ${G.gold}; }
`

const Caret = styled.i`
  position: absolute;
  right: 14px;
  top: 50%;
  width: 0;
  height: 0;
  margin-top: ${({ $open }) => ($open ? '-4px' : '-2px')};
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: ${({ $open }) => ($open ? '0' : `6px solid ${G.sand}`)};
  border-bottom: ${({ $open }) => ($open ? `6px solid ${G.sand}` : '0')};
`

const List = styled.ul`
  position: fixed;
  z-index: 3000;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  overflow-y: auto;
  border: 1px solid ${G.line};
  border-radius: 4px;
  background: ${G.panel};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  &::-webkit-scrollbar { width: 7px; }
  &::-webkit-scrollbar-thumb { border-radius: 10px; background: #41586d; }
  &::-webkit-scrollbar-track { background: #0c151d; }
`

const Item = styled.li`
  padding: 9px 12px;
  font-size: 14px;
  line-height: 18px;
  color: ${({ $active }) => ($active ? G.gold : '#fff')};
  background: ${({ $focus }) => ($focus ? G.panel2 : 'transparent')};
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`
